/**
 * ============================================================
 * ALL-ACCESS-HUB × AVI INTEGRATION ENGINE v2.0
 * ============================================================
 * Powers real-time, context-aware news injection into AVI's
 * responses using All-Access-Hub's full Explore feed catalog.
 *
 * Handles all user conversation patterns:
 * - Direct topic queries ("what's new with Bitcoin?")
 * - Category-level digests ("give me tech news")
 * - Multi-topic queries ("AI and crypto today")
 * - Morning briefings ("morning briefing" / "news roundup")
 * - Trending queries ("what's trending?")
 * - Recency checks ("any updates on Tesla?")
 * - Follow-up awareness ("tell me more" / "what about OpenAI?")
 * - Casual mentions ("I'm thinking about investing in Solana...")
 * - Analysis requests ("what do analysts say about Apple stock?")
 * ============================================================
 */

import { HUB_PROMPTS } from '../prompts/all-access-hub';
import { EXPLORE_FEEDS } from '../data/exploreFeeds';

// ─── Types ────────────────────────────────────────────────────────────────────

type NewsIntent =
    | 'direct_topic'      // "what's new with AI?" / "latest on Bitcoin"
    | 'category_digest'   // "tech news" / "sports today" / "finance update"
    | 'multi_topic'       // "AI and crypto" / "Apple and Google news"
    | 'morning_briefing'  // "morning briefing" / "news roundup" / "catch me up"
    | 'trending'          // "what's trending?" / "what's hot right now?"
    | 'follow_up'         // "tell me more" / "what else?" / "go deeper"
    | 'recency_check'     // "any updates?" / "latest X" / "breaking news"
    | 'analysis'          // "what do analysts think?" / "market analysis"
    | 'none';             // Not news-related at all

interface NewsMatchResult {
    intent: NewsIntent;
    matchedTopics: string[];      // Feed keys from EXPLORE_FEEDS
    matchedCategory: string | null;
    isRecencyEnhanced: boolean;   // User asked for "latest", "breaking", etc.
    isMultiTopic: boolean;
}

interface FetchedArticle {
    title: string;
    description: string;
    link: string;
    sourceName: string;
    topicName: string;
}

// ─── Shared State ─────────────────────────────────────────────────────────────

let exploreFeeds: Record<string, any> = { ...EXPLORE_FEEDS };
let lastMatchedTopicKey: string | null = null;         // For follow-up awareness
let lastMatchedCategory: string | null = null;         // For category follow-ups
const feedCache: Map<string, { articles: FetchedArticle[]; ts: number }> = new Map();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minute RSS cache TTL

// ─── Intent Detection Signals ─────────────────────────────────────────────────

const NEWS_INTENT_SIGNALS = [
    'news', 'latest', 'update', 'what\'s new', 'whats new', 'tell me about',
    'what happened', 'recent', 'trending', 'breaking', 'today', 'this week',
    'this month', 'what do you know about', 'briefing', 'roundup', 'summary',
    'headlines', 'update me', 'catch me up', 'what\'s going on', 'whats going on',
    'any updates', 'anything new', 'talk to me about', 'fill me in', 'inform me',
    'what\'s happening', 'whats happening', 'have you heard', 'did you see',
    'report', 'analysis', 'analysts', 'opinions on', 'take on', 'thoughts on',
    'thinking about', 'curious about', 'interested in', 'learn about'
];

const RECENCY_SIGNALS = [
    'latest', 'breaking', 'just now', 'just happened', 'right now', 'today',
    'this morning', 'this week', 'this year', 'recently', 'new', 'fresh',
    'current', 'now', 'live'
];

const BRIEFING_SIGNALS = [
    'briefing', 'roundup', 'catch me up', 'morning news', 'daily news',
    'news roundup', 'news summary', 'whats going on today', 'what\'s going on today',
    'news today', 'headlines today', 'fill me in', 'what did i miss',
    'overview', 'digest'
];

const TRENDING_SIGNALS = [
    'trending', 'what\'s hot', 'whats hot', 'popular right now',
    'viral', 'blowing up', 'everybody talking about', 'everyone talking about',
    'most talked about', 'hot right now', 'top stories'
];

const FOLLOW_UP_SIGNALS = [
    'tell me more', 'more about that', 'what else', 'go deeper', 'elaborate',
    'expand on that', 'continue', 'keep going', 'and what about', 'more details',
    'dive deeper', 'dig into', 'go on', 'what about that'
];

// Category-to-keyword signals for category-level digest queries
const CATEGORY_SIGNALS: Record<string, string[]> = {
    'Technology': ['tech', 'technology', 'software', 'hardware', 'devices', 'tech news', 'ai news', 'tech world'],
    'Politics': ['politics', 'political', 'government', 'elections', 'legislation', 'political news', 'congress', 'senate'],
    'Business Leaders': ['business leaders', 'ceo news', 'executive news', 'billionaire', 'founders news'],
    'Celebrities': ['celebrity', 'celebrities', 'entertainment news', 'pop culture', 'hollywood', 'star news'],
    'Sports': ['sports', 'sports news', 'game results', 'athletics', 'sports today', 'sports update'],
    'Finance': ['finance', 'financial', 'money', 'markets', 'economy', 'economic', 'financial news', 'market news', 'stocks'],
    'Cryptocurrency': ['crypto', 'cryptocurrency', 'crypto news', 'blockchain news', 'defi news', 'altcoins', 'web3 news'],
    'Social Media': ['social media', 'social platforms', 'social media news', 'platform updates', 'social news'],
    'Health': ['health', 'healthcare', 'wellness', 'medical', 'fitness news', 'health news'],
    'Science': ['science', 'scientific', 'research news', 'discovery news', 'space news', 'science today'],
    'Environment': ['environment', 'climate', 'green news', 'sustainability', 'eco news', 'planet news'],
    'Lifestyle': ['lifestyle', 'culture', 'fashion news', 'travel news', 'food news'],
};

// Related topic graph — if user asks about X, also show Y
const TOPIC_RELATIONS: Record<string, string[]> = {
    'artificial intelligence': ['chatgpt', 'sam altman', 'elon musk', 'nvidia', 'google'],
    'chatgpt': ['artificial intelligence', 'sam altman', 'openai'],
    'bitcoin': ['ethereum', 'cryptocurrency', 'defi', 'web3'],
    'ethereum': ['bitcoin', 'defi', 'nfts', 'web3', 'solana'],
    'elon musk': ['tesla', 'x (twitter)', 'artificial intelligence', 'spacex'],
    'tesla': ['elon musk', 'nvidia', 'electric vehicles', 'stock market'],
    'apple': ['google', 'microsoft', 'samsung', 'apps', 'gaming'],
    'google': ['artificial intelligence', 'apple', 'microsoft', 'youtube'],
    'microsoft': ['artificial intelligence', 'google', 'apple', 'gaming'],
    'nvidia': ['artificial intelligence', 'gaming', 'stock market'],
    'stock market': ['investing', 'economy', 'bitcoin', 'inflation', 'real estate'],
    'tiktok': ['instagram', 'youtube', 'snapchat', 'social media'],
    'mark zuckerberg': ['instagram', 'x (twitter)', 'artificial intelligence', 'meta'],
    'taylor swift': ['beyoncé', 'harry styles', 'music', 'celebrities'],
    'donald trump': ['us politics', 'congress', 'geopolitics', 'economy'],
    'marketing': ['entrepreneurship', 'social media', 'e-commerce', 'small business'],
    'formula 1': ['basketball', 'football', 'soccer', 'sports'],
    'fitness': ['nutrition', 'wellness', 'weight loss', 'healthy eating'],
    'defi': ['ethereum', 'solana', 'web3', 'nfts', 'bitcoin'],
};

// ─── Bridge Initialization ────────────────────────────────────────────────────

export function initializeHubIntegration() {
    const HUB_ORIGIN = 'http://localhost:5177';

    if (document.getElementById('all-access-hub-bridge')) return;

    console.log('[Hub Integration] Starting All-Access-Hub explore feed bridge...');

    const iframe = document.createElement('iframe');
    iframe.src = `${HUB_ORIGIN}/views/integration.html`;
    iframe.style.cssText = 'display:none;width:0;height:0;border:0;position:absolute;';
    iframe.id = 'all-access-hub-bridge';
    document.body.appendChild(iframe);

    window.addEventListener('message', (event) => {
        const isValidOrigin = (
            event.origin === HUB_ORIGIN ||
            event.origin === 'https://all-access-hub.pages.dev' ||
            event.origin === 'https://allaccesshub.com'
        );
        if (!isValidOrigin) return;

        if (event.data?.type === 'SYNC_EXPLORE_FEEDS_RESPONSE') {
            const feeds = event.data.exploreFeeds;
            if (feeds && typeof feeds === 'object') {
                exploreFeeds = feeds;
                const topicCount = Object.keys(feeds).length;
                localStorage.setItem('all_access_hub_explore_feeds', JSON.stringify(feeds));
                console.log(`[Hub Integration] ✅ Synced ${topicCount} Explore topics from All-Access-Hub`);

                // Background warm up top-5 most popular feed categories
                setTimeout(() => warmPopularFeeds(), 2000);
            }
        }
    });

    iframe.onload = () => {
        try {
            iframe.contentWindow?.postMessage({ type: 'REQUEST_EXPLORE_FEEDS' }, HUB_ORIGIN);
        } catch (e) {
            console.error('[Hub Integration] Bridge postMessage failed:', e);
        }
    };

    // Also try to load from localStorage cache while iframe loads
    const cached = localStorage.getItem('all_access_hub_explore_feeds');
    if (cached) {
        try {
            exploreFeeds = JSON.parse(cached);
            console.log(`[Hub Integration] Loaded ${Object.keys(exploreFeeds).length} topics from local cache`);
        } catch (e) { /* ignore */ }
    }
}

// Preemptively warm the cache with the most commonly asked-about feed topics
async function warmPopularFeeds() {
    const POPULAR_KEYS = ['artificial intelligence', 'stock market', 'bitcoin', 'tiktok', 'elon musk'];
    for (const key of POPULAR_KEYS) {
        if (exploreFeeds[key]) {
            fetchFeedArticles(exploreFeeds[key], 1).catch(() => {}); // silent
        }
    }
}

// ─── Intent Classification ────────────────────────────────────────────────────

function classifyNewsIntent(prompt: string): NewsMatchResult {
    const p = prompt.toLowerCase().trim();
    const feeds = exploreFeeds;

    const isRecencyEnhanced = RECENCY_SIGNALS.some(s => p.includes(s));

    // Check follow-up first (before expensive matching)
    if (FOLLOW_UP_SIGNALS.some(s => p.includes(s)) && lastMatchedTopicKey) {
        return {
            intent: 'follow_up',
            matchedTopics: [lastMatchedTopicKey],
            matchedCategory: lastMatchedCategory,
            isRecencyEnhanced,
            isMultiTopic: false
        };
    }

    // Briefing / Digest check
    if (BRIEFING_SIGNALS.some(s => p.includes(s))) {
        // Get top topics across multiple categories for a digest
        const digestTopics = getDigestTopics(p);
        return {
            intent: 'morning_briefing',
            matchedTopics: digestTopics,
            matchedCategory: null,
            isRecencyEnhanced: true,
            isMultiTopic: true
        };
    }

    // Trending check
    if (TRENDING_SIGNALS.some(s => p.includes(s))) {
        const category = detectCategory(p);
        const topicsForCategory = category
            ? Object.keys(feeds).filter(k => feeds[k].category === category).slice(0, 4)
            : getDigestTopics(p).slice(0, 4);
        return {
            intent: 'trending',
            matchedTopics: topicsForCategory,
            matchedCategory: category,
            isRecencyEnhanced: true,
            isMultiTopic: topicsForCategory.length > 1
        };
    }

    // Category-level check
    const detectedCategory = detectCategory(p);
    if (detectedCategory) {
        const categoryTopics = Object.keys(feeds)
            .filter(k => feeds[k].category === detectedCategory)
            .slice(0, 3);
        if (categoryTopics.length > 0) {
            return {
                intent: 'category_digest',
                matchedTopics: categoryTopics,
                matchedCategory: detectedCategory,
                isRecencyEnhanced,
                isMultiTopic: true
            };
        }
    }

    // Direct topic matching — find all matching topics, not just one
    const matched = matchTopicsInPrompt(p, feeds);
    if (matched.length === 0) {
        // Check if has news intent signal even without topic match (general inquiry)
        const hasNewsIntent = NEWS_INTENT_SIGNALS.some(s => p.includes(s));
        return {
            intent: hasNewsIntent ? 'recency_check' : 'none',
            matchedTopics: [],
            matchedCategory: null,
            isRecencyEnhanced,
            isMultiTopic: false
        };
    }

    const isMulti = matched.length > 1;
    return {
        intent: isMulti ? 'multi_topic' : 'direct_topic',
        matchedTopics: matched,
        matchedCategory: feeds[matched[0]]?.category || null,
        isRecencyEnhanced,
        isMultiTopic: isMulti
    };
}

// Detect an explicit category name in a query
function detectCategory(prompt: string): string | null {
    for (const [category, signals] of Object.entries(CATEGORY_SIGNALS)) {
        if (signals.some(s => prompt.includes(s))) return category;
    }
    return null;
}

// Match ALL topics in a prompt (allows multi-topic)
function matchTopicsInPrompt(prompt: string, feeds: Record<string, any>): string[] {
    const matched: string[] = [];
    for (const [key, config] of Object.entries(feeds)) {
        const topicName = (config.topic || '').toLowerCase();
        const keywords: string[] = Array.isArray(config.keywords) ? config.keywords : [];
        if (
            prompt.includes(topicName) ||
            prompt.includes(key.toLowerCase()) ||
            keywords.some((kw: string) => prompt.includes(kw.toLowerCase()))
        ) {
            if (!matched.includes(key)) matched.push(key);
            if (matched.length >= 4) break; // Cap at 4 topics for performance
        }
    }
    return matched;
}

// Build a cross-category digest set
function getDigestTopics(prompt: string): string[] {
    const feeds = exploreFeeds;
    const category = detectCategory(prompt);
    if (category) {
        return Object.keys(feeds).filter(k => feeds[k].category === category).slice(0, 5);
    }
    // Cross-category: 1 from each of the top 5 categories
    const priority = ['Technology', 'Finance', 'Politics', 'Sports', 'Cryptocurrency', 'Social Media'];
    const result: string[] = [];
    for (const cat of priority) {
        const topicInCat = Object.keys(feeds).find(k => feeds[k].category === cat);
        if (topicInCat) result.push(topicInCat);
    }
    return result;
}

// ─── RSS Fetching ─────────────────────────────────────────────────────────────

async function fetchFeedArticles(
    config: any,
    maxPerSource = 2
): Promise<FetchedArticle[]> {
    if (!config?.sources?.length) return [];

    const cacheKey = config.topic;
    const cachedEntry = feedCache.get(cacheKey);
    if (cachedEntry && (Date.now() - cachedEntry.ts < CACHE_TTL_MS)) {
        return cachedEntry.articles;
    }

    const sources = config.sources.slice(0, 3); // Max 3 sources per topic
    const CORS_PROXIES = [
        (url: string) => `https://all-access-hub-proxy.gvtechhub.workers.dev/?url=${encodeURIComponent(url)}`,
        (url: string) => `https://corsproxy.io/?${encodeURIComponent(url)}`,
        (url: string) => `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
    ];

    const fetchPromises = sources.map(async (src: any): Promise<FetchedArticle[]> => {
        for (const proxyFn of CORS_PROXIES) {
            try {
                const res = await fetch(proxyFn(src.url), {
                    signal: AbortSignal.timeout(6000)
                });
                if (!res.ok) continue;
                const xml = await res.text();
                return parseRSSItems(xml, src.name, config.topic, maxPerSource);
            } catch {
                continue; // Try next proxy
            }
        }
        return [];
    });

    const results = await Promise.allSettled(fetchPromises);
    const articles = results
        .filter(r => r.status === 'fulfilled')
        .flatMap(r => (r as PromiseFulfilledResult<FetchedArticle[]>).value);

    feedCache.set(cacheKey, { articles, ts: Date.now() });
    return articles;
}

function parseRSSItems(
    xml: string,
    sourceName: string,
    topicName: string,
    maxItems: number
): FetchedArticle[] {
    const items: FetchedArticle[] = [];
    const matches = xml.matchAll(/<item>([\s\S]*?)<\/item>/g);

    for (const match of matches) {
        const itemXml = match[1];
        const clean = (str: string) =>
            str
                .replace(/<!\[CDATA\[([\s\S]*?)\]\]>/g, '$1')
                .replace(/<[^>]*>/g, '')
                .replace(/&amp;/g, '&')
                .replace(/&lt;/g, '<')
                .replace(/&gt;/g, '>')
                .replace(/&quot;/g, '"')
                .replace(/&#39;/g, "'")
                .trim();

        const title = clean(itemXml.match(/<title>([\s\S]*?)<\/title>/)?.[1] || '');
        const desc = clean(itemXml.match(/<description>([\s\S]*?)<\/description>/)?.[1] || '');
        const link = clean(itemXml.match(/<link>([\s\S]*?)<\/link>/)?.[1] || '');

        if (!title) continue;

        items.push({
            title,
            description: desc.length > 200 ? desc.substring(0, 200) + '...' : desc,
            link,
            sourceName,
            topicName
        });

        if (items.length >= maxItems) break;
    }
    return items;
}

// ─── Context Block Formatters ─────────────────────────────────────────────────

function formatSingleTopicBlock(topicName: string, articles: FetchedArticle[]): string {
    if (articles.length === 0) return '';
    const lines = articles.map(a =>
        `• **${a.title}** *(${a.sourceName})*\n  ${a.description || 'No summary available.'}`
    ).join('\n\n');
    return `### 📰 LIVE NEWS: ${topicName.toUpperCase()}\n${lines}`;
}

function formatCategoryDigestBlock(category: string, articlesByTopic: Map<string, FetchedArticle[]>): string {
    const sections: string[] = [];
    for (const [topic, articles] of articlesByTopic) {
        if (articles.length === 0) continue;
        const topLines = articles.slice(0, 2).map(a => `  • **${a.title}** *(${a.sourceName})*`).join('\n');
        sections.push(`**${topic}**\n${topLines}`);
    }
    if (sections.length === 0) return '';
    return `### 📰 LIVE ${category.toUpperCase()} NEWS DIGEST\n${sections.join('\n\n')}`;
}

function formatBriefingBlock(articlesByTopic: Map<string, FetchedArticle[]>): string {
    const sections: string[] = [];
    for (const [topic, articles] of articlesByTopic) {
        if (articles.length === 0) continue;
        const top = articles[0];
        sections.push(`**${topic.toUpperCase()}**: ${top.title} *(${top.sourceName})*`);
    }
    if (sections.length === 0) return '';
    return `### 🗞️ YOUR LIVE NEWS BRIEFING\n${sections.join('\n')}`;
}

function formatTrendingBlock(category: string | null, articlesByTopic: Map<string, FetchedArticle[]>): string {
    const label = category ? `TRENDING IN ${category.toUpperCase()}` : 'WHAT\'S TRENDING NOW';
    const items: string[] = [];
    for (const [topic, articles] of articlesByTopic) {
        const top = articles[0];
        if (top) items.push(`🔥 **${topic}** — ${top.title} *(${top.sourceName})*`);
    }
    if (items.length === 0) return '';
    return `### ${label}\n${items.join('\n')}`;
}

// ─── Main Context Injection Function ─────────────────────────────────────────

/**
 * Primary integration entry point.
 * Called before every AVI response to inject live context when relevant.
 */
export async function injectAllAccessHubContext(prompt: string): Promise<string> {
    try {
        // Refresh feeds from localStorage if in-memory is empty
        if (Object.keys(exploreFeeds).length === 0) {
            const raw = localStorage.getItem('all_access_hub_explore_feeds');
            if (raw) exploreFeeds = JSON.parse(raw);
        }

        if (Object.keys(exploreFeeds).length === 0) {
            console.warn('[Hub Integration] No explore feeds loaded yet. Skipping context injection.');
            return '';
        }

        const result = classifyNewsIntent(prompt);

        if (result.intent === 'none' || result.matchedTopics.length === 0) {
            // Last resort: if prompt has news signals but no specific topic match,
            // inject a minimal headline block from the default digest
            const hasNewsWords = NEWS_INTENT_SIGNALS.some(s => prompt.toLowerCase().includes(s));
            if (!hasNewsWords) return '';
        }

        console.log(`[Hub Integration] Intent: ${result.intent} | Topics: [${result.matchedTopics.join(', ')}]`);

        const articlesByTopic = new Map<string, FetchedArticle[]>();

        // Fetch articles for all matched topics
        const maxPerSource = result.intent === 'morning_briefing' ? 1 : 2;
        const fetchTasks = result.matchedTopics.slice(0, 4).map(async (key) => {
            const config = exploreFeeds[key];
            if (!config) return;
            const articles = await fetchFeedArticles(config, maxPerSource);
            if (articles.length > 0) {
                articlesByTopic.set(config.topic, articles);
            }
        });

        await Promise.all(fetchTasks);

        if (articlesByTopic.size === 0) return '';

        // Update follow-up memory
        if (result.matchedTopics.length > 0) {
            lastMatchedTopicKey = result.matchedTopics[0];
            lastMatchedCategory = result.matchedCategory;
        }

        // Choose the right formatter based on intent
        let contextBlock = '';

        switch (result.intent) {
            case 'morning_briefing':
                contextBlock = formatBriefingBlock(articlesByTopic);
                break;
            case 'trending':
                contextBlock = formatTrendingBlock(result.matchedCategory, articlesByTopic);
                break;
            case 'category_digest':
                contextBlock = formatCategoryDigestBlock(result.matchedCategory!, articlesByTopic);
                break;
            case 'multi_topic':
                // Show each topic as its own block, capped at 2 topics for readability
                const blocks: string[] = [];
                for (const [topic, articles] of articlesByTopic) {
                    blocks.push(formatSingleTopicBlock(topic, articles.slice(0, 2)));
                    if (blocks.length >= 2) break;
                }
                contextBlock = blocks.join('\n\n');
                break;
            case 'follow_up':
                // Go deeper — fetch more sources and more articles
                const followKey = lastMatchedTopicKey!;
                const followConfig = exploreFeeds[followKey];
                if (followConfig) {
                    const deepArticles = await fetchFeedArticles(followConfig, 3);
                    const relatedKeys = TOPIC_RELATIONS[followKey] || [];
                    // Also pull 1 article from a related topic
                    for (const relKey of relatedKeys.slice(0, 1)) {
                        const relConfig = exploreFeeds[relKey];
                        if (relConfig) {
                            const relArticles = await fetchFeedArticles(relConfig, 1);
                            deepArticles.push(...relArticles);
                        }
                    }
                    contextBlock = formatSingleTopicBlock(followConfig.topic + ' (Deep Dive)', deepArticles);
                }
                break;
            case 'analysis':
            case 'recency_check':
            case 'direct_topic':
            default:
                for (const [topic, articles] of articlesByTopic) {
                    contextBlock = formatSingleTopicBlock(topic, articles);
                    break; // Just the first (most specific) topic
                }
                break;
        }

        if (!contextBlock) return '';

        // Build the final system prompt injection
        const recencyNote = result.isRecencyEnhanced
            ? `\nThese are **live, real-time** headlines fetched just now.`
            : '';

        return HUB_PROMPTS.NEWS_INJECTION_BLOCK(contextBlock, recencyNote);
    } catch (err) {
        console.error('[Hub Integration] Error during context injection:', err);
        return '';
    }
}

/**
 * Returns a prebuilt list of what topics AVI has access to,
 * for AVI to reference when a user asks "what can you tell me about?"
 */
export function getAvailableTopicsForAVI(): string {
    if (Object.keys(exploreFeeds).length === 0) return '';
    const categories = new Map<string, string[]>();
    for (const config of Object.values(exploreFeeds)) {
        const cat = (config as any).category || 'General';
        const topic = (config as any).topic || '';
        if (!categories.has(cat)) categories.set(cat, []);
        categories.get(cat)!.push(topic);
    }
    const lines = [...categories.entries()].map(([cat, topics]) =>
        `**${cat}**: ${topics.slice(0, 5).join(', ')}${topics.length > 5 ? ` +${topics.length - 5} more` : ''}`
    );
    return HUB_PROMPTS.TOPIC_DISCOVERY(lines);
}
